/**
 * One block of the dispatcher's assistant conversation, drawn in the portal's
 * own language.
 *
 * WHY NOT ChatWidget/ChatMessage. That component renders the same block union
 * and is the right one on the dashboard, but it is built out of react-bootstrap
 * buttons and Font Awesome glyphs — neither of which exists inside `.pp-root`,
 * where every colour is a `--pp-*` token and every icon is inline SVG. Rendered
 * here it would come out unstyled and 1.5× too big for a thumb. The REDUCER is
 * shared (`useChatAgent`) and so is the transport (`ChatAgentService`); only the
 * drawing is this surface's own, which is the same split `TripCards` already
 * uses across the three portals.
 *
 * WHAT IS DELIBERATELY NOT DRAWN, and why:
 *   `usage`   per-turn tokens and cents. A dispatcher is not buying the
 *             answer, and a cost chip under every reply is noise on a 390px
 *             screen. The event still arrives and is still stored.
 *   `map`     needs a Mapbox token in the page. The portal is opened on
 *             airport wifi and its first paint is deliberately free of
 *             third-party round trips (see Components/Icons).
 *   `proposal` / `commit_result`
 *             the booking-ingest grid, which is a desk job on a wide screen
 *             (Components/IngestWorkflow). Rendering a 20-row proposal in a
 *             bottom sheet would invite somebody to commit bookings they
 *             cannot read.
 * Unknown kinds render nothing, exactly as they do on the dashboard — the
 * agent can add an event without a UI release.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CheckIcon, ReceiptIcon } from '../Components/Icons';

/** What a fragment is labelled with, so it cannot read as a whole answer. */
export const TRUNCATED_NOTE = 'Stopped before the end';

/**
 * Links leave the portal, so they open in a new tab — a dispatcher who
 * followed one in place would lose the board behind it. Tables are wrapped
 * in their own scroller: the agent answers "what is on tomorrow" with a
 * four-column table and a phone is 390px wide, so the choice is a sideways
 * scroll or an unreadable squeeze.
 */
const MD_COMPONENTS = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  // `tabIndex={0}` for the same reason the thread has one: the wrapper scrolls
  // sideways, and a region only a pointer can scroll is unreachable from a
  // keyboard. The table's own caption would be nicer than a generic label, but
  // the agent does not send one, so the columns speak for it.
  table: ({ children }) => (
    <div
      className="pp-ai__table"
      role="group"
      aria-label="Table"
      // As on the thread: a region that scrolls has to be reachable without a
      // pointer, and this rule cannot see that it scrolls.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <table>{children}</table>
    </div>
  ),
};

function TextBlock({ block }) {
  const user = block.role === 'user';

  return (
    <div className={`pp-ai__msg pp-ai__msg--${user ? 'me' : 'them'}`}>
      {user ? (
        // A dispatcher's own words, verbatim. Markdown here would reformat
        // what they typed, and `1. ` at the start of a question is a list to
        // a parser and a sentence to a person.
        <p className="pp-ai__said">{block.content}</p>
      ) : (
        <div className="pp-ai__md">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
            {block.content || ''}
          </ReactMarkdown>
        </div>
      )}
      {/* The whole point of the flag: an answer that stopped mid-sentence
          says so ON ITSELF, not only in a banner further down that scrolls
          away. */}
      {block.truncated ? (
        <p className="pp-ai__cut" data-testid="assistant-truncated">{TRUNCATED_NOTE}</p>
      ) : null}
      {block.streaming ? <span className="pp-ai__caret" aria-hidden="true" /> : null}
    </div>
  );
}

/**
 * What the agent is doing right now, one line per tool call. Kept because a
 * dispatcher waiting eight seconds for "reading the board" is waiting
 * patiently, and a dispatcher waiting eight seconds for nothing taps again.
 */
function ToolBlock({ block }) {
  return (
    <p className={`pp-ai__tool pp-ai__tool--${block.status}`}>
      {block.status === 'done' ? <CheckIcon size={14} width={3} /> : null}
      <span>{block.summary || block.name}</span>
    </p>
  );
}

function QuestionBlock({ block, onAnswer }) {
  return (
    <div className="pp-ai__ask">
      <div className="pp-ai__md">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {block.prompt || ''}
        </ReactMarkdown>
      </div>
      {block.choices?.length ? (
        <div className="pp-ai__choices">
          {block.choices.map((choice) => (
            <button
              key={choice.value}
              type="button"
              className="pp-btn pp-btn--sm pp-btn--outline"
              disabled={block.answered}
              onClick={() => onAnswer(block.id, choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A write the agent wants to make, and the two buttons that decide it.
 *
 * This one is NOT optional on a phone. The agent holds write tools and the
 * backend's first call is always `confirm=False`, so this card is the only
 * place a dispatcher sees what is about to change before it changes. Drawn
 * as a card with the arguments listed, because "Approve" over a sentence
 * nobody can check is not approval.
 */
function ApprovalBlock({ block, onDecide }) {
  const decided = block.decision != null;
  const details = Object.entries(block.details || {});

  const shown = (value) => {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className={`pp-ai__approval${decided ? ' is-decided' : ''}`}>
      <p className="pp-ai__approval-action">{block.action}</p>
      {details.length ? (
        <dl className="pp-ai__approval-rows">
          {details.map(([key, value]) => (
            <React.Fragment key={key}>
              <dt>{key}</dt>
              <dd>{shown(value)}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      <div className="pp-actions">
        <button
          type="button"
          className="pp-btn pp-btn--sm pp-btn--outline pp-btn--grow"
          disabled={decided}
          onClick={() => onDecide(block.id, 'reject')}
        >
          {block.decision === 'reject' ? 'Cancelled' : 'Cancel'}
        </button>
        <button
          type="button"
          className="pp-btn pp-btn--sm pp-btn--dark pp-btn--grow"
          disabled={decided}
          onClick={() => onDecide(block.id, 'approve')}
        >
          {block.decision === 'approve' ? 'Approved' : 'Approve'}
        </button>
      </div>
    </div>
  );
}

function FileBlock({ block }) {
  return (
    <a
      className="pp-ai__file"
      href={block.url}
      target="_blank"
      rel="noopener noreferrer"
      download={block.filename}
    >
      <ReceiptIcon size={18} />
      <span>{block.filename}</span>
    </a>
  );
}

function ImageBlock({ block }) {
  return (
    <figure className="pp-ai__figure">
      <img src={block.url} alt={block.altText || ''} />
      {block.caption ? <figcaption>{block.caption}</figcaption> : null}
    </figure>
  );
}

export default function AssistantBlock({ block, onAnswer, onDecideApproval }) {
  switch (block.kind) {
    case 'text': return <TextBlock block={block} />;
    case 'tool': return <ToolBlock block={block} />;
    case 'question': return <QuestionBlock block={block} onAnswer={onAnswer} />;
    case 'approval': return <ApprovalBlock block={block} onDecide={onDecideApproval} />;
    case 'file': return <FileBlock block={block} />;
    case 'image': return <ImageBlock block={block} />;
    default: return null;
  }
}
